import React, { useCallback, useMemo, useState } from "react";
import {
  Autocomplete,
  AutocompleteCollection,
  AutocompleteEmpty,
  AutocompleteGroup,
  AutocompleteGroupLabel,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompletePopup,
  AutocompleteStatus,
  Box,
  Icon,
  Text,
  toastManager,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  type Omittable,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { HotkeyTooltipLabel } from "#app/components/keyboard-shortcuts/hotkey-tooltip-label";
import type { EntityData } from "#app/lib/entity-visuals";
import {
  useGlobalSearch,
  type GlobalSearchHit,
  type SearchKind,
} from "#app/lib/hooks/use-global-search";
import { useResolveAddressSuggestion } from "#app/lib/hooks/use-resolve-address-suggestion";
import {
  useSlashSearchTarget,
  VIEW_SEARCH_SHORTCUT,
} from "#app/lib/hooks/use-slash-search";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SearchEvent, trackEvent } from "#app/lib/analytics";

import { useRecordMapPlaceVisit } from "./use-recent-map-places";

const MAP_SEARCH_KINDS = ["address", "issue"] satisfies SearchKind[];
const RESULT_VISUAL_SIZE = 28;

type MapSearchItem = {
  value: string;
  hit: GlobalSearchHit;
};

type MapSearchGroup = {
  value: string;
  label: string;
  items: MapSearchItem[];
};

type MapSearchBarProps = Omittable<{
  /** Focus the field on mount — set by a host that just unfolded it from a
   *  glyph button, so the press that opened it is the press that focuses. */
  autoFocus?: boolean;
  /** The field is done: a pick landed, Escape on an empty query, or focus
   *  left with no results open. A host that folds the field to a button
   *  hides it again on this. */
  onCollapse?: () => void;
  /** The field holds focus. A host that lays a click-away layer over its
   *  canvas keys it on this, so the press that leaves the search does not
   *  also land on the canvas. */
  onActiveChange?: (active: boolean) => void;
  /** A control living at the field's start cap (see
   *  `AutocompleteInput.startAction`) — the mobile map keeps its shell
   *  trigger there so the top row is one island. */
  startAction?: React.ReactNode;
  backgroundColor: string;
  /** Resting hairline resolved against the map (see MapTileTheme.controlBorder). */
  borderColor: string;
  onLocationSelect: (
    coords: { latitude: number; longitude: number },
    addressId?: string,
  ) => void;
  onProjectSelect: (projectId: string) => void;
}>;

function toSearchItems(hits: GlobalSearchHit[]): MapSearchItem[] {
  return hits.map((hit) => ({
    value: `${hit.kind}-${hit.id}`,
    hit,
  }));
}

function MapSearchResult({ item }: { item: MapSearchItem }) {
  const { hit } = item;

  if (hit.kind === "address") {
    return (
      <Box align="center" direction="row" gap="sm" minWidth={0}>
        <Box
          align="center"
          height={RESULT_VISUAL_SIZE}
          justify="center"
          shrink={false}
          width={RESULT_VISUAL_SIZE}
        >
          <Icon color="muted" name="map-pin" size="sm" />
        </Box>
        <Text size="sm" truncate={1}>
          {hit.name}
        </Text>
      </Box>
    );
  }

  const entity: EntityData = {
    kind: "issue",
    id: hit.id,
    issue: {
      name: hit.name,
      icon: { icon: hit.iconName, color: hit.iconColor },
    },
    workflowId: hit.workflowId ?? undefined,
  };

  return (
    <EntityTag
      avatarSize="sm"
      description={hit.subtitle ?? undefined}
      entity={entity}
      ghost={false}
      interactive={false}
      padded
      size="sm"
      visualBoxSize={RESULT_VISUAL_SIZE}
      weight="regular"
    />
  );
}

/** Search projects and Google Places without opening the global launcher. */
export function MapSearchBar({
  autoFocus = false,
  backgroundColor,
  borderColor,
  onActiveChange,
  onCollapse,
  onLocationSelect,
  onProjectSelect,
  startAction,
}: MapSearchBarProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  // The map's one view search: bare `/` focuses it, and the tooltip
  // advertises the keycap only while this field is the hotkey's target.
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isSlashTarget = useSlashSearchTarget(inputRef);
  // The field stays mounted while folded (display: none), so the DOM's own
  // autofocus never re-fires; the unfold focuses it by hand.
  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);
  const { query, setQuery, results, loading, clear } = useGlobalSearch({
    kinds: MAP_SEARCH_KINDS,
  });
  const resolveAddress = useResolveAddressSuggestion();
  // A PROJECT pick is a visit for the Map menu's Recent rows: the reader
  // named the project, and the focus that follows may land on no pin (the
  // project's pin can sit outside the current filters). An address pick is
  // not recorded here — the pin the focus opens is the canonical address,
  // and the map screen records that.
  const recordVisit = useRecordMapPlaceVisit();

  const groups = useMemo<MapSearchGroup[]>(() => {
    const nextGroups: MapSearchGroup[] = [
      {
        value: "addresses",
        label: "Addresses",
        items: toSearchItems(results.addresses.items),
      },
      {
        value: "projects",
        label: "Projects",
        items: toSearchItems(results.issues.items),
      },
    ];
    return nextGroups.filter((group) => group.items.length > 0);
  }, [results.addresses.items, results.issues.items]);

  const closeSearch = useCallback(() => {
    setOpen(false);
    clear();
    onCollapse?.();
  }, [clear, onCollapse]);

  const selectItem = useCallback(
    async (item: MapSearchItem) => {
      const { hit } = item;
      closeSearch();
      trackEvent({
        context: AnalyticsContext.Search,
        event: SearchEvent.SelectResult,
        label: "map_search",
        data: { kind: hit.kind, from: "results", onMap: true },
      });

      if (hit.kind === "issue") {
        const placed =
          typeof hit.lat === "number" &&
          Number.isFinite(hit.lat) &&
          typeof hit.lng === "number" &&
          Number.isFinite(hit.lng)
            ? { lat: hit.lat, lng: hit.lng }
            : null;
        recordVisit({
          kind: "issue",
          id: hit.id,
          name: hit.name,
          lat: placed?.lat ?? null,
          lng: placed?.lng ?? null,
          workflowId: hit.workflowId,
          iconName: hit.iconName,
          iconColor: hit.iconColor,
        });
        if (placed) {
          onLocationSelect({
            latitude: placed.lat,
            longitude: placed.lng,
          });
        } else {
          onProjectSelect(hit.id);
        }
        return;
      }

      try {
        const resolved = await resolveAddress(hit.id, hit.name);
        if (resolved?.latLng) {
          onLocationSelect(
            {
              latitude: resolved.latLng.lat,
              longitude: resolved.latLng.lng,
            },
            resolved.addressId,
          );
          return;
        }
        toastManager.add({
          title: "Address not found",
          type: "error",
        });
      } catch (error) {
        toastMutationError(error, "Failed to open address");
      }
    },
    [closeSearch, onLocationSelect, onProjectSelect, recordVisit, resolveAddress],
  );

  const input = (
    <AutocompleteInput
      aria-label="Search Map"
      backgroundColor={backgroundColor}
      onBlur={() => {
        onActiveChange?.(false);
        // Focus left with nothing to pick from: the folded host takes
        // the field back. With results open the blur belongs to a
        // pick in flight, and selectItem's closeSearch reports it.
        if (!open || query.trim().length === 0) onCollapse?.();
      }}
      onFocus={() => onActiveChange?.(true)}
      onKeyDown={(event) => {
        if (event.key === "Escape" && query.trim().length === 0) {
          onCollapse?.();
        }
      }}
      borderColor={borderColor}
      clearTooltip="Clear search"
      placeholder="Search Map"
      ref={inputRef}
      showClear
      // Desktop rests on the default 32px rung — the rail workspace
      // tile's height; mobile matches a horizontal dock's touch height.
      size={isMobile ? "2xl" : undefined}
      startAction={startAction}
      type="search"
      variant="outline"
    />
  );

  return (
    <Autocomplete
      autoHighlight
      itemToStringValue={(item: MapSearchItem) => item.hit.name}
      items={groups}
      mode="none"
      onOpenChange={setOpen}
      onValueChange={(next, details) => {
        if (details.reason === "item-press") return;
        setQuery(next);
        setOpen(next.trim().length > 0);
      }}
      open={open && query.trim().length > 0}
      openOnInputClick
      value={query}
    >
      {/* The tooltip is the keycap's home, so it only exists for a pointer
        * that hovers: on mobile it would also open over a start action
        * inside the group, naming the drawer button "Search Map". */}
      {isMobile ? (
        input
      ) : (
        <Tooltip>
          <TooltipTrigger render={<Box />}>{input}</TooltipTrigger>
          <TooltipPopup side="bottom">
            <HotkeyTooltipLabel
              keys={isSlashTarget ? VIEW_SEARCH_SHORTCUT : undefined}
              label="Search Map"
            />
          </TooltipPopup>
        </Tooltip>
      )}
      <AutocompletePopup>
        <AutocompleteStatus>{loading ? "Searching…" : null}</AutocompleteStatus>
        <AutocompleteList>
          {(group: MapSearchGroup) => (
            <AutocompleteGroup
              items={group.items}
              key={group.value}
            >
              <AutocompleteGroupLabel>{group.label}</AutocompleteGroupLabel>
              <AutocompleteCollection>
                {(item: MapSearchItem) => (
                  <AutocompleteItem
                    key={item.value}
                    onClick={() => void selectItem(item)}
                    value={item}
                  >
                    <MapSearchResult item={item} />
                  </AutocompleteItem>
                )}
              </AutocompleteCollection>
            </AutocompleteGroup>
          )}
        </AutocompleteList>
        <AutocompleteEmpty>
          {loading ? null : "No addresses or Projects found."}
        </AutocompleteEmpty>
      </AutocompletePopup>
    </Autocomplete>
  );
}
